/**
 * Load a spreadsheet of moulds into the catalog.
 *
 * Four steps, and none of them guesses on the person's behalf:
 *   1. choose a file;
 *   2. say which sheets, where the moulds are, and what each column means -
 *      every column is listed as imported, required or ignored, with why;
 *   3. see what the server will do with every row - nothing is written yet;
 *   4. import: the server writes the rows in one transaction, the photos follow
 *      one by one, and the result shown is the server's own account, checked by
 *      asking it again.
 *
 * It used to write into this device's copy and report success from there,
 * which is how 408 moulds could be "imported" while the server refused every
 * one. Importing now needs a connection, and says so.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertCircle, CheckCircle2, FileSpreadsheet, Info, Loader2, Upload } from 'lucide-react';
import type { ImportAction, ImportCommitResponse, ImportPreviewResponse, ImportProblem, ImportRowPlan, ImportRunRow } from '@mouldtrack/shared';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Checkbox, Select } from '../../components/ui/field.js';
import { Badge, Code } from '../../components/ui/primitives.js';
import { db } from '../../db/schema.js';
import { ApiClientError, commitImport, completeImport, isNetworkError, previewImport, uploadMouldPhoto } from '../../api/client.js';
import { downloadImportTemplate } from './exportMoulds.js';
import { syncNow } from '../../sync/engine.js';
import {
  IMPORT_FIELDS,
  buildRows,
  defaultUse,
  headingStatus,
  readImportFile,
  saveMapping,
  savedMapping,
  suggestLocation,
  type BuiltImport,
  type ColumnUse,
  type SourceFile,
} from './importMoulds.js';

type Phase = 'choose' | 'setup' | 'checking' | 'preview' | 'saving' | 'photos' | 'verifying' | 'done';

/** One picture still to send: which mould, and which picture on which row. */
interface PhotoJob {
  mouldId: string;
  row: BuiltImport['rows'][number];
  index: number;
}

const OFFLINE = 'Importing needs a connection to the server, and this device is offline. Nothing was changed.';

function messageOf(e: unknown, fallback: string): string {
  if (isNetworkError(e)) return OFFLINE;
  if (e instanceof ApiClientError) return e.message;
  return e instanceof Error ? e.message : fallback;
}

export function MouldImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('choose');
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<SourceFile | null>(null);
  const [sheets, setSheets] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<Record<string, ColumnUse>>(() => savedMapping());
  const [locationId, setLocationId] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [built, setBuilt] = useState<BuiltImport | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [commit, setCommit] = useState<ImportCommitResponse | null>(null);
  const [run, setRun] = useState<ImportRunRow | null>(null);
  const [photoProgress, setPhotoProgress] = useState({ done: 0, total: 0, added: 0, already: 0 });
  const [confirmed, setConfirmed] = useState<{ present: number; missing: string[] } | null>(null);
  const [failedPhotos, setFailedPhotos] = useState<PhotoJob[]>([]);
  const [unsupported, setUnsupported] = useState<ImportProblem[]>([]);
  const [closeNote, setCloseNote] = useState<string | null>(null);

  const locations = useLiveQuery(() => db.locations.toArray(), []);
  const liveLocations = useMemo(
    () => (locations ?? []).filter((l) => !l.deletedAt).sort((a, b) => a.name.localeCompare(b.name)),
    [locations],
  );

  const use = (key: string): ColumnUse => overrides[key] ?? defaultUse(key);

  function reset() {
    setPhase('choose');
    setError(null);
    setSource(null);
    setSheets([]);
    setLocationId('');
    setOverwrite(false);
    setBuilt(null);
    setPreview(null);
    setCommit(null);
    setRun(null);
    setConfirmed(null);
    setFailedPhotos([]);
    setUnsupported([]);
    setCloseNote(null);
    setPhotoProgress({ done: 0, total: 0, added: 0, already: 0 });
  }

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  async function onFile(file: File) {
    setError(null);
    try {
      const read = await readImportFile(file);
      const usable = read.sheets.filter((s) => s.headerRow && s.dataRows > 0);
      if (usable.length === 0) {
        setError('No sheet in that file has a heading row with mould columns ("MOLD NUMBER", "MOLD NAME" and so on) in its first ten rows.');
        return;
      }
      setSource(read);
      setSheets(usable.map((s) => s.name));
      setLocationId(suggestLocation(read, liveLocations)?.id ?? '');
      setPhase('setup');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.');
    }
  }

  // Every heading on the chosen sheets, once, with how many values sit under it.
  const headings = useMemo(() => {
    if (!source) return [];
    const byKey = new Map<string, { key: string; text: string; sheets: string[]; filled: number }>();
    for (const sheet of source.sheets.filter((s) => sheets.includes(s.name) && s.headerRow)) {
      for (const h of sheet.headings) {
        const entry = byKey.get(h.key) ?? { key: h.key, text: h.text, sheets: [], filled: 0 };
        if (!entry.sheets.includes(sheet.name)) entry.sheets.push(sheet.name);
        for (let r = sheet.headerRow + 1; r <= sheet.rowCount; r++) {
          const v = sheet.cell(r, h.col);
          if (v !== null && String(v).trim() !== '') entry.filled++;
        }
        byKey.set(h.key, entry);
      }
    }
    return [...byKey.values()];
  }, [source, sheets]);

  const hasCode = headings.some((h) => use(h.key) === 'code' || use(h.key) === 'codeAndName');

  async function check() {
    if (!source) return;
    setError(null);
    const next = buildRows(source, sheets, use);
    setBuilt(next);
    if (next.rows.length === 0) {
      setError(next.sheetProblems.join(' ') || 'No rows to import on the chosen sheets.');
      return;
    }
    if (next.rows.length > 5000) {
      setError(`The chosen sheets hold ${next.rows.length} rows; one import takes up to 5,000. Untick a sheet, or split the file.`);
      return;
    }
    saveMapping(overrides);
    setPhase('checking');
    try {
      setPreview(
        await previewImport({ rows: strip(next), options: { locationId: locationId || null, overwrite } }),
      );
      setPhase('preview');
    } catch (e) {
      setError(messageOf(e, 'The server could not check the file.'));
      setPhase('setup');
    }
  }

  /** Send pictures a few at a time; retry what is worth retrying; stop cleanly when the connection goes. */
  async function sendPhotos(jobs: PhotoJob[], runId: string, carried: { added: number; already: number }) {
    const progress = { done: 0, total: jobs.length, added: carried.added, already: carried.already };
    setPhotoProgress({ ...progress });
    const failed: Array<{ job: PhotoJob; reason: string }> = [];
    let offline = false;
    let next = 0;

    const sendOne = async (job: PhotoJob): Promise<'added' | 'already'> => {
      const image = job.row.images[job.index]!;
      const blob = new Blob([image.bytes as BlobPart], { type: image.mimeType ?? '' });
      for (let attempt = 1; ; attempt++) {
        try {
          const out = await uploadMouldPhoto(job.mouldId, blob, { importRunId: runId });
          return out.duplicate ? 'already' : 'added';
        } catch (e) {
          // A dropped connection, a busy or restarting server: worth another
          // go after a pause. A refusal ("not a picture") is not.
          const transient =
            isNetworkError(e) ||
            (e instanceof ApiClientError && (e.status >= 500 || e.status === 429 || e.code === 'BAD_RESPONSE'));
          if (!transient || attempt >= 3) throw e;
          await new Promise((r) => setTimeout(r, 1500 * attempt));
        }
      }
    };

    const worker = async () => {
      while (next < jobs.length) {
        const job = jobs[next++]!;
        if (offline) {
          failed.push({ job, reason: 'not sent - the connection was lost' });
        } else {
          try {
            const outcome = await sendOne(job);
            progress[outcome]++;
          } catch (e) {
            if (isNetworkError(e)) offline = true;
            failed.push({ job, reason: messageOf(e, 'could not be uploaded') });
          }
        }
        progress.done++;
        setPhotoProgress({ ...progress });
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    return failed;
  }

  /** Close the run on the server with the pictures that did not arrive, and check the result. */
  async function finish(
    result: ImportCommitResponse,
    rows: BuiltImport,
    failed: Array<{ job: PhotoJob; reason: string }>,
    notShown: ImportProblem[],
  ) {
    setPhase('verifying');
    setFailedPhotos(failed.map((f) => f.job));
    const photoProblems: ImportProblem[] = [
      ...notShown,
      ...failed.map(({ job, reason }) => ({
        sheet: job.row.sheet,
        row: job.row.sheetRow,
        code: job.row.assetCode,
        reason: `Picture ${job.index + 1}: ${reason}`,
        photo: true,
      })),
    ];
    try {
      setRun(await completeImport(result.runId, { photoProblems }));
      setCloseNote(null);
    } catch {
      // The rows are saved either way; only the run's photo tally lags.
      setCloseNote('The import history could not be updated with the photo count. The moulds and photos themselves are saved.');
    }
    try {
      const again = await previewImport({ rows: strip(rows), options: { overwrite: false } });
      const missing = again.rows
        .filter((r, i) => result.rows[i]?.action !== 'blocked' && (r.action === 'create' || r.action === 'restore'))
        .map((r) => r.assetCode);
      setConfirmed({ present: again.rows.filter((r) => r.action !== 'blocked').length - missing.length, missing });
    } catch {
      setConfirmed(null);
    }
    void syncNow();
    setPhase('done');
  }

  async function runImport() {
    if (!source || !built) return;
    if (!navigator.onLine) {
      setError(OFFLINE);
      return;
    }
    setError(null);
    setPhase('saving');
    let result: ImportCommitResponse;
    try {
      result = await commitImport({
        rows: strip(built),
        options: { locationId: locationId || null, overwrite },
        fileName: source.fileName,
        sheetNames: sheets,
      });
      setCommit(result);
    } catch (e) {
      // A refusal is definite: nothing was written. A lost connection or a
      // proxy timeout is not - the server may have finished after we stopped
      // listening. Say which, and that trying again is safe either way.
      const unsure =
        isNetworkError(e) ||
        (e instanceof ApiClientError && ([502, 503, 504].includes(e.status) || e.code === 'BAD_RESPONSE'));
      setError(
        unsure
          ? 'The connection was lost while saving, so it is not certain whether the server finished. Look under Imports to see, or simply import the same file again - that is safe: nothing is ever added twice.'
          : messageOf(e, 'The import could not be saved. Nothing was written.'),
      );
      setPhase('preview');
      return;
    }

    setPhase('photos');
    const notShown: ImportProblem[] = [];
    const jobs: PhotoJob[] = [];
    built.rows.forEach((row, i) => {
      const plan = result.rows[i];
      for (const name of row.unsupportedImages) {
        notShown.push({
          sheet: row.sheet,
          row: row.sheetRow,
          code: row.assetCode,
          reason: `A picture is ${name}, which a browser cannot display. Save it as JPEG or PNG and import again.`,
          photo: true,
        });
      }
      if (!plan?.mouldId || plan.action === 'blocked') return;
      row.images.forEach((_, index) => jobs.push({ mouldId: plan.mouldId!, row, index }));
    });
    setUnsupported(notShown);
    const failed = await sendPhotos(jobs, result.runId, { added: 0, already: 0 });
    await finish(result, built, failed, notShown);
  }

  /** From the result screen: send again only the pictures that did not arrive. */
  async function retryPhotos() {
    if (!commit || !built || failedPhotos.length === 0) return;
    if (!navigator.onLine) {
      setError(OFFLINE);
      return;
    }
    setError(null);
    setPhase('photos');
    const failed = await sendPhotos(failedPhotos, commit.runId, photoProgress);
    await finish(commit, built, failed, unsupported);
  }

  const counts = preview?.counts;
  const writes = counts ? counts.create + counts.restore + counts.update : 0;
  const keptValues = preview?.rows.reduce((n, r) => n + r.kept.length, 0) ?? 0;
  const placing = preview?.rows.filter((r) => r.willPlace).length ?? 0;
  const busy = phase === 'checking' || phase === 'saving' || phase === 'photos' || phase === 'verifying';

  // Closing the tab mid-import would stop the photos half-sent. The browser asks first.
  useEffect(() => {
    if (!busy) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return; // closing mid-import would hide its only report
        onOpenChange(next);
      }}
      size="lg"
      title="Import moulds from a spreadsheet"
      description="Every sheet and every photo in the file. Nothing is written until you have seen what will happen."
      footer={
        phase === 'done' ? (
          <>
            {failedPhotos.length ? (
              <Button variant="secondary" onClick={() => void retryPhotos()}>
                Retry {failedPhotos.length} failed photo{failedPhotos.length === 1 ? '' : 's'}
              </Button>
            ) : null}
            <Button variant="primary" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            {phase === 'setup' || phase === 'checking' ? (
              <Button
                variant="primary"
                onClick={() => void check()}
                loading={phase === 'checking'}
                disabled={sheets.length === 0 || !hasCode}
              >
                Check the file
              </Button>
            ) : null}
            {phase === 'preview' || phase === 'saving' || phase === 'photos' || phase === 'verifying' ? (
              <Button
                variant="primary"
                onClick={() => void runImport()}
                loading={phase !== 'preview'}
                disabled={phase === 'preview' && writes === 0 && placing === 0 && (built?.photos ?? 0) === 0}
              >
                {writes > 0
                  ? `Import ${writes} mould${writes === 1 ? '' : 's'}`
                  : placing > 0
                    ? `Place ${placing} mould${placing === 1 ? '' : 's'}`
                    : (built?.photos ?? 0) > 0
                      ? 'Add any missing photos'
                      : 'Nothing new to import'}
              </Button>
            ) : null}
          </>
        )
      }
    >
      <div className="flex flex-col gap-4 pb-2">
        {phase === 'choose' ? (
          <>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm,.xlsb,.xls,.csv,.tsv,.txt"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border-strong px-6 py-10 text-center hover:bg-surface-sunken"
            >
              <FileSpreadsheet className="size-8 text-muted-foreground" aria-hidden />
              <span className="font-medium">Choose an Excel file</span>
              <span className="text-sm text-muted-foreground">
                .xlsx, .xlsm, .xlsb, .xls, .csv or .tsv. Every picture on a row comes across from the Excel formats.
              </span>
            </button>
            <p className="text-sm text-muted-foreground">
              The expected format is the client's Master Sheet, as MOLD LIST_63 (S.NO, Mould Ownership, Customer,
              IMAGE, CODE AND NAME, MOLD NUMBER, MOLD NAME … MOLD SIZE (LxBxH) … QR NUMBER, Location) - the same
              headings an export has.{' '}
              <button
                type="button"
                className="font-medium text-primary underline-offset-2 hover:underline"
                onClick={() => void downloadImportTemplate()}
              >
                Download a blank template
              </button>
              . Other layouts work too - every column is matched by its heading.
            </p>
            <p className="text-sm text-muted-foreground">
              A mould is matched by its code, never by its name. Whatever the sheet has is imported and the rest is
              left blank to fill in later; a blank cell never clears a value, and a value someone entered by hand is
              kept.
            </p>
          </>
        ) : null}

        {source && phase !== 'choose' ? (
          <div className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="size-4 text-muted-foreground" aria-hidden />
            <span className="font-medium">{source.fileName}</span>
          </div>
        ) : null}

        {source && (phase === 'setup' || phase === 'checking') ? (
          <>
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">Sheets</h3>
              {source.sheets.map((s) => (
                <Checkbox
                  key={s.name}
                  label={`“${s.name}”`}
                  description={
                    s.headerRow
                      ? `${s.dataRows} row${s.dataRows === 1 ? '' : 's'} with data, ${s.pictures} picture${s.pictures === 1 ? '' : 's'} · headings on row ${s.headerRow}`
                      : 'No heading row found - cannot be imported'
                  }
                  disabled={!s.headerRow}
                  checked={sheets.includes(s.name)}
                  onChange={(e) =>
                    setSheets((cur) => (e.target.checked ? [...cur, s.name] : cur.filter((n) => n !== s.name)))
                  }
                />
              ))}
            </section>

            <section className="flex flex-col gap-1.5">
              <h3 className="text-sm font-semibold">Where are these moulds?</h3>
              <Select
                aria-label="Location for these moulds"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                options={[
                  { value: '', label: 'Do not set a location' },
                  ...liveLocations.map((l) => ({ value: l.id, label: l.name, hint: l.type === 'VENDOR' ? 'supplier' : undefined })),
                ]}
              />
              <p className="text-xs text-muted-foreground">
                The sheet has no location column. The location chosen here is recorded for moulds that have never been
                scanned; a mould that has been scanned stays where its last scan put it.
              </p>
            </section>

            <section className="flex flex-col gap-1.5">
              <h3 className="text-sm font-semibold">Columns</h3>
              {!hasCode ? (
                <p className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
                  Choose which column holds the mould code - usually “MOLD NUMBER”.
                </p>
              ) : null}
              <div className="max-h-80 overflow-auto rounded-xl border border-border">
                <table className="w-full border-collapse text-sm">
                  <thead className="sticky top-0 z-10 bg-surface-sunken">
                    <tr>
                      <Th>Column in the file</Th>
                      <Th>Imported as</Th>
                      <Th>Status</Th>
                      <Th className="text-right">Values</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {headings.map((h) => {
                      const current = use(h.key);
                      const status = headingStatus(h.key, current);
                      return (
                        <tr key={h.key}>
                          <td className="px-3 py-1.5">
                            <span className="font-medium">{h.text}</span>
                            {sheets.length > 1 && h.sheets.length < sheets.length ? (
                              <span className="block text-xs text-muted-foreground">only on “{h.sheets.join('”, “')}”</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-1.5">
                            <Select
                              aria-label={`Use of column ${h.text}`}
                              value={current}
                              onChange={(e) => setOverrides((o) => ({ ...o, [h.key]: e.target.value as ColumnUse }))}
                              options={[
                                { value: 'ignore', label: 'Ignore' },
                                ...IMPORT_FIELDS.map((f) => ({ value: f.field, label: f.label })),
                              ]}
                              className="h-8 min-w-44 text-sm"
                            />
                          </td>
                          <td className="px-3 py-1.5">
                            <StatusBadge status={status.status} />
                            {status.reason ? <span className="block text-xs text-muted-foreground">{status.reason}</span> : null}
                          </td>
                          <td className="px-3 py-1.5 text-right tabular text-muted-foreground">{h.filled}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            <Checkbox
              label="Also replace values someone entered by hand"
              description="Off: the sheet fills blanks and updates values that came from an earlier sheet, but a value a person typed into MouldTrack is kept. A blank cell never clears anything. Turn on only when this sheet is the corrected version of everything."
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
            />
          </>
        ) : null}

        {preview && counts && built && (phase === 'preview' || phase === 'saving' || phase === 'photos' || phase === 'verifying') ? (
          <PreviewView preview={preview} built={built} keptValues={keptValues} placing={placing} overwrite={overwrite} />
        ) : null}

        {phase === 'saving' ? (
          <Progress text="Saving on the server - all rows in one go, or none of them…" />
        ) : null}
        {phase === 'photos' ? (
          <Progress
            text={`Sending photos: ${photoProgress.done} of ${photoProgress.total}`}
            value={photoProgress.total ? photoProgress.done / photoProgress.total : 1}
          />
        ) : null}
        {phase === 'verifying' ? <Progress text="Checking the result with the server…" /> : null}

        {phase === 'done' && commit ? (
          <DoneView
            commit={commit}
            run={run}
            built={built}
            photos={photoProgress}
            confirmed={confirmed}
            failedPhotos={failedPhotos.length}
            closeNote={closeNote}
            onRetry={() => void retryPhotos()}
            onClose={() => onOpenChange(false)}
          />
        ) : null}

        {error ? (
          <p role="alert" className="flex items-start gap-2 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}

        {phase === 'setup' || phase === 'preview' ? (
          <div className="flex gap-2">
            {phase === 'preview' ? (
              <Button variant="ghost" size="sm" onClick={() => setPhase('setup')}>
                Back to columns and location
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" onClick={reset}>
              <Upload className="size-3.5" /> Choose a different file
            </Button>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

/** Rows as the server takes them - the pictures travel separately. */
function strip(built: BuiltImport) {
  return built.rows.map(({ images: _images, unsupportedImages: _unsupported, ...row }) => row);
}

function Th({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <th className={`px-3 py-2 text-left text-xs font-semibold text-muted-foreground ${className}`}>{children}</th>;
}

function StatusBadge({ status }: { status: ReturnType<typeof headingStatus>['status'] }) {
  if (status === 'required') return <Badge tone="primary">Required</Badge>;
  if (status === 'imported') return <Badge tone="success">Imported</Badge>;
  if (status === 'client-ignored') return <Badge tone="warning">Ignored</Badge>;
  if (status === 'not-imported') return <Badge tone="neutral">Not imported</Badge>;
  return <Badge tone="neutral">Ignored</Badge>;
}

const ACTION_LABEL: Record<ImportAction, string> = {
  create: 'Add',
  restore: 'Bring back',
  update: 'Fill in',
  unchanged: 'Already in',
  blocked: 'Problem',
};

function ActionBadge({ action }: { action: ImportAction }) {
  const tone = action === 'create' ? 'primary' : action === 'restore' ? 'info' : action === 'update' ? 'warning' : action === 'blocked' ? 'danger' : 'neutral';
  return <Badge tone={tone}>{ACTION_LABEL[action]}</Badge>;
}

function PreviewView({
  preview,
  built,
  keptValues,
  placing,
  overwrite,
}: {
  preview: ImportPreviewResponse;
  built: BuiltImport;
  keptValues: number;
  placing: number;
  overwrite: boolean;
}) {
  const [filter, setFilter] = useState<'all' | ImportAction | 'kept'>('all');
  const partsOf = useMemo(
    () => new Map(built.rows.filter((r) => r.partRows?.length).map((r) => [`${r.sheet}|${r.sheetRow}`, r.partRows!])),
    [built],
  );
  const { counts } = preview;
  const rows = preview.rows.filter((r) =>
    filter === 'all' ? true : filter === 'kept' ? r.kept.length > 0 : r.action === filter,
  );

  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat label="New" value={counts.create} tone="primary" />
        <Stat label="Bring back" value={counts.restore} />
        <Stat label="Fill in" value={counts.update} tone="warning" />
        <Stat label="Already in" value={counts.unchanged} />
        <Stat label="Problems" value={counts.blocked} tone={counts.blocked ? 'danger' : undefined} />
      </div>

      <div className="rounded-xl border border-border bg-surface-sunken/40 px-3 py-2 text-sm">
        <b className="tabular">{built.dataRowsSeen}</b> rows with data = <b className="tabular">{preview.rows.length}</b> checked by the
        server. {built.photos} photo{built.photos === 1 ? '' : 's'} to send
        {built.unsupportedPhotos ? `, ${built.unsupportedPhotos} in a format a browser cannot show (reported, not sent)` : ''}.{' '}
        {preview.newMasters
          ? `${preview.newMasters} new master value${preview.newMasters === 1 ? '' : 's'} (customers, projects…) will be added.`
          : 'Every customer, project and other value already exists.'}
        {placing ? ` ${placesSummary(preview.rows)}.` : ''}
        {preview.rows.some((r) => r.placeNote)
          ? ` ${preview.rows.filter((r) => r.placeNote).length} keep their location - see the rows marked below.`
          : ''}
      </div>

      {built.combined.moulds > 0 ? (
        <Callout tone="info">
          <b>
            {built.combined.moulds} mould{built.combined.moulds === 1 ? ' is' : 's are'} written over several rows
          </b>{' '}
          - one row per part, with the S.NO on the first only. Each is imported as one mould, every part in its
          name and cavities as the sheet lists them (1+1+1+1). {built.combined.rows} row{built.combined.rows === 1 ? '' : 's'} folded in.
        </Callout>
      ) : null}

      {built.placeholderCodes > 0 ? (
        <Callout tone="danger">
          <b>
            {built.placeholderCodes} row{built.placeholderCodes === 1 ? ' has' : 's have'} “NA” or similar as the mould number.
          </b>{' '}
          {built.placeholderCodes === 1 ? 'It is' : 'They are'} listed as problems and not imported: a mould needs its real number, which is
          also what its QR label carries.
        </Callout>
      ) : null}

      {built.sizesLeftOut.length > 0 ? (
        <Callout tone="warning">
          <b>
            {built.sizesLeftOut.length} mould size{built.sizesLeftOut.length === 1 ? ' is' : 's are'} not a size, so{' '}
            {built.sizesLeftOut.length === 1 ? 'it is' : 'they are'} left out:
          </b>{' '}
          {built.sizesLeftOut
            .slice(0, 5)
            .map((s) => `${s.code || 'no number'} (row ${s.row}: “${s.value}”)`)
            .join(', ')}
          {built.sizesLeftOut.length > 5 ? `, and ${built.sizesLeftOut.length - 5} more` : ''}. A size has numbers in
          it, such as 450X560X500, and is engraved as “Mold Dimension:”. The rest of each row imports. Correct the size
          in the sheet and import again, or type it on the mould.
        </Callout>
      ) : null}

      {counts.restore > 0 ? (
        <Callout tone="info">
          <b>{counts.restore} of these were deleted</b> and will come back as they were - same record, same history -
          rather than as new moulds.
        </Callout>
      ) : null}

      {keptValues > 0 && !overwrite ? (
        <Callout tone="info">
          <b>
            {keptValues} value{keptValues === 1 ? '' : 's'} on the sheet differ{keptValues === 1 ? 's' : ''} from the catalog
          </b>{' '}
          and were entered by hand in MouldTrack, so they will be kept. See “Values kept” below. To use the sheet's
          instead, go back and tick “Also replace values someone entered by hand”.
        </Callout>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Row by row</h3>
        <Select
          aria-label="Show rows"
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          options={[
            { value: 'all', label: `All rows (${preview.rows.length})` },
            { value: 'create', label: `New (${counts.create})` },
            { value: 'restore', label: `Bring back (${counts.restore})` },
            { value: 'update', label: `Fill in (${counts.update})` },
            { value: 'kept', label: `Values kept (${preview.rows.filter((r) => r.kept.length).length})` },
            { value: 'unchanged', label: `Already in (${counts.unchanged})` },
            { value: 'blocked', label: `Problems (${counts.blocked})` },
          ]}
          className="h-8 w-52 text-sm"
        />
      </div>
      <div className="max-h-80 overflow-auto rounded-xl border border-border">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 bg-surface-sunken">
            <tr>
              <Th>Row</Th>
              <Th>Mould</Th>
              <Th>Action</Th>
              <Th>Detail</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.slice(0, 600).map((row) => (
              <tr key={`${row.sheet}-${row.sheetRow}`}>
                <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground tabular">{row.sheetRow}</td>
                <td className="px-3 py-1.5">
                  {row.assetCode ? <Code className="text-xs">{row.assetCode}</Code> : null}{' '}
                  <span className="text-muted-foreground">{row.name !== row.assetCode ? row.name : ''}</span>
                </td>
                <td className="px-3 py-1.5">
                  <ActionBadge action={row.action} />
                </td>
                <td className="px-3 py-1.5 text-muted-foreground">
                  <RowDetail row={row} parts={partsOf.get(`${row.sheet}|${row.sheetRow}`)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 600 ? <p className="px-3 py-2 text-xs text-muted-foreground">First 600 of {rows.length} shown.</p> : null}
      </div>
    </>
  );
}

/** "12 moulds will be recorded at Patalganga, 3 at Andheri" */
function placesSummary(rows: ImportRowPlan[]): string {
  const by = new Map<string, number>();
  for (const r of rows) if (r.willPlace && r.placeAt) by.set(r.placeAt, (by.get(r.placeAt) ?? 0) + 1);
  const parts = [...by.entries()].sort((a, b) => b[1] - a[1]).map(([place, n]) => `${n} at ${place}`);
  const total = [...by.values()].reduce((a, b) => a + b, 0);
  return `${total} mould${total === 1 ? '' : 's'} will be recorded at a location (${parts.join(', ')})`;
}

function RowDetail({ row, parts: partRows }: { row: ImportRowPlan; parts?: number[] }) {
  if (row.action === 'blocked') return <span className="text-danger">{row.reason}</span>;
  const parts: string[] = [];
  if (partRows?.length) parts.push(`${partRows.length + 1} parts (rows ${row.sheetRow}–${partRows[partRows.length - 1]})`);
  if (row.writes.length && row.action !== 'create') parts.push(`sets ${row.writes.slice(0, 4).join(', ')}${row.writes.length > 4 ? ` +${row.writes.length - 4}` : ''}`);
  if (row.newMasters.length) parts.push(`new: ${row.newMasters.slice(0, 2).join(', ')}${row.newMasters.length > 2 ? '…' : ''}`);
  if (row.photoCount) parts.push(`${row.photoCount} photo${row.photoCount === 1 ? '' : 's'}`);
  return (
    <>
      {parts.join(' · ')}
      {row.kept.map((k) => (
        <span key={k.field} className="block text-xs">
          {k.field}: keeps “{k.catalog}”{k.byHand ? ' (entered by hand)' : ''}, sheet says “{k.sheet}”
        </span>
      ))}
      {row.placeAt && row.action !== 'create' ? <span className="block text-xs">placed at {row.placeAt}</span> : null}
      {row.placeNote ? <span className="block text-xs text-warning">{row.placeNote}</span> : null}
    </>
  );
}

function DoneView({
  commit,
  run,
  built,
  photos,
  confirmed,
  failedPhotos,
  closeNote,
  onRetry,
  onClose,
}: {
  commit: ImportCommitResponse;
  run: ImportRunRow | null;
  built: BuiltImport | null;
  photos: { done: number; total: number; added: number; already: number };
  confirmed: { present: number; missing: string[] } | null;
  failedPhotos: number;
  closeNote: string | null;
  onRetry: () => void;
  onClose: () => void;
}) {
  const problems = run?.problems ?? commit.rows.filter((r) => r.action === 'blocked').map((r) => ({ sheet: r.sheet, row: r.sheetRow, code: r.assetCode, reason: r.reason ?? '' }));
  const photoFailures = failedPhotos;
  const clean = problems.length === 0 && (!confirmed || confirmed.missing.length === 0);
  const c = commit.counts;

  return (
    <>
      <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${clean ? 'border-border bg-success-subtle' : 'border-warning/40 bg-warning-subtle'}`}>
        {clean ? <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-success" aria-hidden /> : <AlertCircle className="mt-0.5 size-5 shrink-0 text-warning" aria-hidden />}
        <div className="text-sm">
          <p className="font-medium">
            {clean ? 'Saved on the server. Every row is accounted for.' : `Saved on the server, with ${problems.length} row${problems.length === 1 ? '' : 's'} to look at.`}
          </p>
          <p className="text-muted-foreground">
            Stored in the database, not just on this device - it stays after refresh, sign-out or a server restart, and
            every other device receives it on its next sync.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-border">
        <Line label="Rows in the file" value={built?.dataRowsSeen ?? commit.rows.length} />
        <Line label="Added" value={c.create} tone="ok" />
        <Line label="Brought back (were deleted)" value={c.restore} />
        <Line label="Filled in" value={c.update} />
        <Line label="Already in the catalog" value={c.unchanged} />
        <Line label="Skipped - see problems below" value={c.blocked} tone={c.blocked ? 'warn' : undefined} />
        <Line label="New master values" value={commit.mastersAdded} />
        <Line label="Recorded at the chosen location" value={commit.placed} />
        <Line label="Photos added" value={photos.added} tone="ok" />
        <Line label="Photos already on the mould" value={photos.already} />
        <Line label="Photos that failed" value={photoFailures} tone={photoFailures ? 'bad' : undefined} />
        <Line
          label="Confirmed on the server afterwards"
          value={confirmed ? confirmed.present : 0}
          tone={confirmed ? 'ok' : 'warn'}
          last
          note={confirmed ? undefined : 'could not re-check'}
        />
      </div>

      {failedPhotos ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-danger/40 bg-danger-subtle px-3 py-2 text-sm text-danger">
          <span>
            <b>
              {failedPhotos} photo{failedPhotos === 1 ? '' : 's'} did not arrive.
            </b>{' '}
            The moulds are saved; only these pictures are missing. Sending them again is safe - a picture already
            there is never stored twice.
          </span>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Retry failed photos
          </Button>
        </div>
      ) : null}

      {closeNote ? <Callout tone="info">{closeNote}</Callout> : null}

      {confirmed?.missing.length ? (
        <Callout tone="danger">
          <b>Not found on the server afterwards:</b> {confirmed.missing.slice(0, 20).join(', ')}
          {confirmed.missing.length > 20 ? ` and ${confirmed.missing.length - 20} more` : ''}.
        </Callout>
      ) : null}

      {problems.length ? (
        <div className="max-h-56 overflow-auto rounded-xl border border-warning/40">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-warning-subtle">
              <tr>
                <Th>Sheet</Th>
                <Th>Row</Th>
                <Th>Mould</Th>
                <Th>Problem</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {problems.map((p, i) => (
                <tr key={i}>
                  <td className="px-3 py-1.5 text-muted-foreground">{p.sheet}</td>
                  <td className="px-3 py-1.5 tabular text-muted-foreground">{p.row}</td>
                  <td className="px-3 py-1.5">{p.code ? <Code className="text-xs">{p.code}</Code> : '-'}</td>
                  <td className="px-3 py-1.5">{p.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Info className="size-3.5" aria-hidden /> This import is kept in the{' '}
        <Link to="/imports" onClick={onClose} className="underline">
          import history
        </Link>
        .
      </p>
    </>
  );
}

function Callout({ tone, children }: { tone: 'info' | 'warning' | 'danger'; children: ReactNode }) {
  return (
    <div
      className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-sm ${
        tone === 'danger'
          ? 'border-danger/40 bg-danger-subtle text-danger'
          : tone === 'warning'
            ? 'border-warning/40 bg-warning-subtle text-warning'
            : 'border-info/25 bg-info-subtle text-info'
      }`}
    >
      <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </div>
  );
}

function Progress({ text, value }: { text: string; value?: number }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" aria-hidden />
        {text}
      </p>
      {value !== undefined ? (
        <div className="h-1.5 overflow-hidden rounded-full bg-surface-sunken">
          <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${Math.round(value * 100)}%` }} />
        </div>
      ) : null}
    </div>
  );
}

function Line({ label, value, tone, last, note }: { label: string; value: number; tone?: 'ok' | 'warn' | 'bad'; last?: boolean; note?: string }) {
  return (
    <div className={`flex items-baseline justify-between px-3 py-1.5 text-sm ${last ? '' : 'border-b border-border'}`}>
      <span className="text-muted-foreground">{label}</span>
      <span
        className={
          tone === 'bad'
            ? 'tabular font-semibold text-danger'
            : tone === 'warn'
              ? 'tabular font-semibold text-warning-foreground'
              : tone === 'ok'
                ? 'tabular font-semibold text-success'
                : 'tabular font-semibold'
        }
      >
        {note ?? value}
      </span>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'primary' | 'warning' | 'danger' }) {
  return (
    <div className="rounded-xl border border-border bg-surface px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={
          tone === 'primary'
            ? 'text-xl font-semibold text-primary'
            : tone === 'warning'
              ? 'text-xl font-semibold text-warning-foreground'
              : tone === 'danger'
                ? 'text-xl font-semibold text-danger'
                : 'text-xl font-semibold'
        }
      >
        {value}
      </div>
    </div>
  );
}
